import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaBook, FaBookOpen, FaSearch, FaLink, FaPlus, FaEllipsisV } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import Modal, { Field, Label } from '../../common/Modal';
import ConfirmationModal from '../../common/ConfirmationModal';
import EmptyState from '../../common/EmptyState';
import { Menu, MenuItem, IconButton, TextField, FormControl, InputLabel, Select } from '@mui/material';
import OrgUnitTypeSelector from '../../common/OrgUnitTypeSelector';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  Content,
  ContentWrapper,
  ConfigurationSection,
  SectionTitle,
  OptionsContainer,
  OptionButton,
  LegendSection,
  LegendTitle,
  LegendGrid,
  LegendItem,
  HeaderButton,
  TabContainer,
  TabButton,
  RolesContainer,
  SearchInput,
  SearchContainer,
  SearchIcon,
  RoleCard,
  RoleCardContent,
  RolesHeader,
  RolesContent,
  StructureWrapper
} from './styles';
import { COLORS } from '../../../constants/radar';
import { ROLE_TYPES, ROLE_TYPE_COLORS } from '../../../constants/roleTypes';


const TABS = {
  STRUCTURE: 'structure',
  ROLES: 'roles'
};

const EmployeeOrgConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedFunctionalRoles, setSelectedFunctionalRoles] = useState([]);
  const [selectedOrgRoles, setSelectedOrgRoles] = useState([]);
  const [selectedTechRoles, setSelectedTechRoles] = useState([]);
  const [showLegend, setShowLegend] = useState(false);
  const [loading, setLoading] = useState(true);
  const [legendData, setLegendData] = useState({ gradeLegend: [], roleLegend: [] });
  const [structureData, setStructureData] = useState({
    grades: [],
    positions: [],
    roles: []
  });
  const [activeTab, setActiveTab] = useState(TABS.STRUCTURE);
  const [isGradeModalOpen, setIsGradeModalOpen] = useState(false);
  const [gradeModalMode, setGradeModalMode] = useState('add'); // 'add' or 'edit'
  const [gradeModalData, setGradeModalData] = useState({ label: '', description: '', order: 0 });
  const [selectedGradeId, setSelectedGradeId] = useState(null);
  const [gradeMenuAnchor, setGradeMenuAnchor] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [gradeToDelete, setGradeToDelete] = useState(null);
  const [draggedGrade, setDraggedGrade] = useState(null);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [positionModalMode, setPositionModalMode] = useState('add');
  const [positionModalData, setPositionModalData] = useState({ 
    label: '', 
    description: '', 
    order: 0,
    requiresGrade: true 
  });
  const [selectedPositionId, setSelectedPositionId] = useState(null);
  const [positionMenuAnchor, setPositionMenuAnchor] = useState(null);
  const [isPositionDeleteConfirmationOpen, setIsPositionDeleteConfirmationOpen] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState(null);
  const [draggedPosition, setDraggedPosition] = useState(null);
  const [selectedRoleTypes, setSelectedRoleTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [roleModalMode, setRoleModalMode] = useState('add');
  const [roleModalData, setRoleModalData] = useState({ label: '', description: '', type: '', order: 0 });
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [isRoleDeleteConfirmationOpen, setIsRoleDeleteConfirmationOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);
  const [isRoleSelectionModalOpen, setIsRoleSelectionModalOpen] = useState(false);
  const [roleSelectionType, setRoleSelectionType] = useState(null);
  const [roleSearchQuery, setRoleSearchQuery] = useState('');
  const [roleToDetach, setRoleToDetach] = useState(null);
  const [isRoleDetachConfirmationOpen, setIsRoleDetachConfirmationOpen] = useState(false);
  const [roleOptionMenuAnchor, setRoleOptionMenuAnchor] = useState(null);
  const [selectedOrgUnitType, setSelectedOrgUnitType] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Only fetch legend and structure if org unit type is selected
        if (selectedOrgUnitType) {
          const [legendResponse, structureResponse] = await Promise.all([
            organizationApi.getLegend(),
            organizationApi.getStructure(selectedOrgUnitType)
          ]);
          
          setLegendData(legendResponse);
          setStructureData(structureResponse);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        showAlert('Failed to load configuration data', 'error', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, showAlert, selectedOrgUnitType]);

  useEffect(() => {
    if (activeTab === TABS.STRUCTURE) {
      setShowLegend(false);
    }
  }, [activeTab]);

  const handleGradeSelect = async (grade) => {
    try {
      const gradeId = structureData.grades.find(g => g.label === grade)?.id;
      if (!gradeId) return;

      // Toggle selection: if clicking the same grade, unselect it
      setSelectedGrade(selectedGrade === grade ? null : grade);
    } catch (error) {
      console.error('Failed to update grade:', error);
    }
  };

  const handlePositionSelect = async (position) => {
    try {
      // Toggle selection: if clicking the same position, unselect it
      if (selectedPosition === position) {
        setSelectedPosition(null);
        // Clear role selections when unselecting position
        setSelectedFunctionalRoles([]);
        setSelectedOrgRoles([]);
        setSelectedTechRoles([]);
        return;
      }

      // Set the selected position
      setSelectedPosition(position);
      
      // Clear previous role selections
      setSelectedFunctionalRoles([]);
      setSelectedOrgRoles([]);
      setSelectedTechRoles([]);
    } catch (error) {
      console.error('Failed to update position:', error);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleGradeModalOpen = (mode, grade = null) => {
    setGradeModalMode(mode);
    if (mode === 'edit' && grade) {
      setGradeModalData({ 
        label: grade.label, 
        description: grade.description || '',
        order: grade.order 
      });
      setSelectedGradeId(grade.id);
    } else {
      setGradeModalData({ label: '', description: '', order: 0 });
      setSelectedGradeId(null);
    }
    setIsGradeModalOpen(true);
  };

  const handleGradeModalClose = () => {
    setIsGradeModalOpen(false);
    setGradeModalData({ label: '', description: '', order: 0 });
    setSelectedGradeId(null);
  };

  const handleGradeModalSubmit = async (e) => {
    e.preventDefault();
    try {
      if (gradeModalMode === 'add') {
        await organizationApi.createGrade({
          label: gradeModalData.label,
          description: gradeModalData.description,
          order: parseInt(gradeModalData.order, 10)
        });
        showAlert('Grade created successfully', 'success');
      } else {
        await organizationApi.updateGrade(selectedGradeId, {
          label: gradeModalData.label,
          description: gradeModalData.description,
          order: parseInt(gradeModalData.order, 10)
        });
        showAlert('Grade updated successfully', 'success');
      }
      
      // Refresh data
      const structureResponse = await organizationApi.getStructure(selectedOrgUnitType || undefined);
      setStructureData(structureResponse);
      
      handleGradeModalClose();
    } catch (error) {
      showAlert(error.message || 'Failed to manage grade', 'error', error);
    }
  };

  const handleGradeMenuOpen = (event, grade) => {
    event.stopPropagation();
    setSelectedGradeId(grade.id);
    setGradeMenuAnchor(event.currentTarget);
  };

  const handleGradeMenuClose = () => {
    setGradeMenuAnchor(null);
  };

  const handleGradeDelete = async () => {
    try {
      await organizationApi.deleteGrade(gradeToDelete.id);
      showAlert('Grade deleted successfully', 'success');
      
      // Refresh data
      const structureResponse = await organizationApi.getStructure(selectedOrgUnitType || undefined);
      setStructureData(structureResponse);
      
      setIsDeleteConfirmationOpen(false);
      handleGradeMenuClose();
    } catch (error) {
      showAlert(error.message || 'Failed to delete grade', 'error', error);
    }
  };

  const handleDeleteClick = (grade) => {
    setGradeToDelete(grade);
    setIsDeleteConfirmationOpen(true);
    handleGradeMenuClose();
  };

  const handleDragStart = (e, grade) => {
    setDraggedGrade(grade);
    e.target.style.opacity = '0.4';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedGrade(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetGrade) => {
    e.preventDefault();
    if (!draggedGrade || draggedGrade.id === targetGrade.id) return;

    try {
      const newGrades = [...sortedGrades];
      const draggedIndex = newGrades.findIndex(g => g.id === draggedGrade.id);
      const targetIndex = newGrades.findIndex(g => g.id === targetGrade.id);

      // Reorder the array
      newGrades.splice(draggedIndex, 1);
      newGrades.splice(targetIndex, 0, draggedGrade);

      // Update orders
      const updatedGrades = newGrades.map((grade, index) => ({
        ...grade,
        order: index
      }));

      // Update the backend
      await Promise.all(
        updatedGrades.map(grade =>
          organizationApi.updateGrade(grade.id, {
            label: grade.label,
            description: grade.description,
            order: grade.order
          })
        )
      );

      // Update local state
      setStructureData(prev => ({
        ...prev,
        grades: updatedGrades
      }));

      showAlert('Grade order updated successfully', 'success');
    } catch (error) {
      showAlert('Failed to update grade order', 'error', error);
      console.error('Failed to update grade order:', error);
    }
  };

  const handlePositionModalOpen = (mode, position = null) => {
    setPositionModalMode(mode);
    if (mode === 'edit' && position) {
      setPositionModalData({ 
        label: position.label, 
        description: position.description || '',
        order: position.order,
        requiresGrade: position.requiresGrade 
      });
      setSelectedPositionId(position.id);
    } else {
      setPositionModalData({ label: '', description: '', order: 0, requiresGrade: true });
      setSelectedPositionId(null);
    }
    setIsPositionModalOpen(true);
  };

  const handlePositionModalClose = () => {
    setIsPositionModalOpen(false);
    setPositionModalData({ label: '', description: '', order: 0, requiresGrade: true });
    setSelectedPositionId(null);
  };

  const handlePositionModalSubmit = async (e) => {
    e.preventDefault();
    try {
      if (positionModalMode === 'add') {
        await organizationApi.createPosition({
          label: positionModalData.label,
          description: positionModalData.description,
          order: parseInt(positionModalData.order, 10),
          orgUnitTypeId: selectedOrgUnitType || undefined,
          requiresGrade: positionModalData.requiresGrade
        });
        showAlert('Position created successfully', 'success');
      } else {
        await organizationApi.updatePosition(selectedPositionId, {
          label: positionModalData.label,
          description: positionModalData.description,
          order: parseInt(positionModalData.order, 10),
          requiresGrade: positionModalData.requiresGrade
        });
        showAlert('Position updated successfully', 'success');
      }
      
      // Refresh data
      const structureResponse = await organizationApi.getStructure(selectedOrgUnitType || undefined);
      setStructureData(structureResponse);
      
      handlePositionModalClose();
    } catch (error) {
      showAlert(error.message || 'Failed to manage position', 'error', error);
    }
  };

  const handlePositionMenuOpen = (event, position) => {
    event.stopPropagation();
    setSelectedPositionId(position.id);
    setPositionMenuAnchor(event.currentTarget);
  };

  const handlePositionMenuClose = () => {
    setPositionMenuAnchor(null);
  };

  const handlePositionDelete = async () => {
    try {
      await organizationApi.deletePosition(positionToDelete.id);
      showAlert('Position deleted successfully', 'success');
      
      // Refresh data
      const structureResponse = await organizationApi.getStructure(selectedOrgUnitType || undefined);
      setStructureData(structureResponse);
      
      setIsPositionDeleteConfirmationOpen(false);
      handlePositionMenuClose();
    } catch (error) {
      showAlert(error.message || 'Failed to delete position', 'error', error);
    }
  };

  const handlePositionDeleteClick = (position) => {
    setPositionToDelete(position);
    setIsPositionDeleteConfirmationOpen(true);
    handlePositionMenuClose();
  };

  const handlePositionDragStart = (e, position) => {
    setDraggedPosition(position);
    e.target.style.opacity = '0.4';
  };

  const handlePositionDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedPosition(null);
  };

  const handlePositionDragOver = (e) => {
    e.preventDefault();
  };

  const handlePositionDrop = async (e, targetPosition) => {
    e.preventDefault();
    if (!draggedPosition || draggedPosition.id === targetPosition.id) return;

    try {
      const newPositions = [...sortedPositions];
      const draggedIndex = newPositions.findIndex(p => p.id === draggedPosition.id);
      const targetIndex = newPositions.findIndex(p => p.id === targetPosition.id);

      // Reorder the array
      newPositions.splice(draggedIndex, 1);
      newPositions.splice(targetIndex, 0, draggedPosition);

      // Update orders
      const updatedPositions = newPositions.map((position, index) => ({
        ...position,
        order: index
      }));

      // Update the backend
      await Promise.all(
        updatedPositions.map(position =>
          organizationApi.updatePosition(position.id, {
            label: position.label,
            description: position.description,
            order: position.order
          })
        )
      );

      // Update local state
      setStructureData(prev => ({
        ...prev,
        positions: updatedPositions
      }));

      showAlert('Position order updated successfully', 'success');
    } catch (error) {
      showAlert('Failed to update position order', 'error', error);
      console.error('Failed to update position order:', error);
    }
  };

  const handleRoleTypeToggle = (type) => {
    setSelectedRoleTypes(prev => 
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const getFilteredRoles = () => {
    return structureData.roles
      .filter(role => 
        (selectedRoleTypes.length === 0 || selectedRoleTypes.includes(role.type)) &&
        (searchQuery === '' || role.label.toLowerCase().includes(searchQuery.toLowerCase()))
      )
      .sort((a, b) => a.order - b.order);
  };

  const getPositionsForRole = (roleId) => {
    return structureData.positions
      .filter(position => position.roles.some(r => r.id === roleId))
      .map(position => position.label);
  };

  const handleRoleModalOpen = (mode, role = null) => {
    setRoleModalMode(mode);
    if (mode === 'edit' && role) {
      setRoleModalData({ 
        label: role.label, 
        description: role.description || '',
        type: role.type,
        order: role.order 
      });
      setSelectedRoleId(role.id);
    } else {
      setRoleModalData({ label: '', description: '', type: ROLE_TYPES.FUNCTIONAL, order: 0 });
      setSelectedRoleId(null);
    }
    setIsRoleModalOpen(true);
  };

  const handleRoleModalClose = () => {
    setIsRoleModalOpen(false);
    setRoleModalData({ label: '', description: '', type: ROLE_TYPES.FUNCTIONAL, order: 0 });
    setSelectedRoleId(null);
  };

  const handleRoleModalSubmit = async (e) => {
    e.preventDefault();
    try {
      if (roleModalMode === 'add') {
        await organizationApi.createRole({
          label: roleModalData.label,
          description: roleModalData.description,
          type: roleModalData.type,
          order: parseInt(roleModalData.order, 10)
        });
        showAlert('Role created successfully', 'success');
      } else {
        await organizationApi.updateRole(selectedRoleId, {
          label: roleModalData.label,
          description: roleModalData.description,
          type: roleModalData.type,
          order: parseInt(roleModalData.order, 10)
        });
        showAlert('Role updated successfully', 'success');
      }
      
      // Refresh data
      const structureResponse = await organizationApi.getStructure(selectedOrgUnitType || undefined);
      setStructureData(structureResponse);
      
      handleRoleModalClose();
    } catch (error) {
      showAlert(error.message || 'Failed to manage role', 'error', error);
    }
  };

  const handleRoleMenuOpen = (event, role) => {
    setSelectedRoleId(role.id);
    setRoleToDelete(role);
    setRoleMenuAnchor(event.currentTarget);
  };

  const handleRoleMenuClose = () => {
    setRoleMenuAnchor(null);
  };

  const handleRoleDelete = async () => {
    try {
      await organizationApi.deleteRole(roleToDelete.id);
      showAlert('Role deleted successfully', 'success');
      
      // Refresh data
      const structureResponse = await organizationApi.getStructure(selectedOrgUnitType || undefined);
      setStructureData(structureResponse);
      
      setIsRoleDeleteConfirmationOpen(false);
      handleRoleMenuClose();
    } catch (error) {
      showAlert(error.message || 'Failed to delete role', 'error', error);
    }
  };

  const getAvailableRolesForType = (type) => {
    const selectedPositionObj = structureData.positions.find(p => p.label === selectedPosition);
    return structureData.roles
      .filter(role => 
        role.type === type && 
        role.label.toLowerCase().includes(roleSearchQuery.toLowerCase()) &&
        !selectedPositionObj?.roles.some(r => r.id === role.id)
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const handleRoleLinkClick = (type) => {
    if (!selectedPosition) return;
    setRoleSelectionType(type);
    setRoleSearchQuery('');
    setIsRoleSelectionModalOpen(true);
  };

  const handleRoleSelect = async (role) => {
    try {
      const positionObj = structureData.positions.find(p => p.label === selectedPosition);
      if (!positionObj) return;

      await organizationApi.attachRoleToPosition(positionObj.id, role.id);
      
      // Refresh data
      const structureResponse = await organizationApi.getStructure(selectedOrgUnitType || undefined);
      setStructureData(structureResponse);
      
      showAlert('Role attached successfully', 'success');
    } catch (error) {
      showAlert(error.message || 'Failed to attach role', 'error', error);
    }
  };

  const handleRoleOptionMenuOpen = (event, role) => {
    event.stopPropagation();
    setRoleToDetach(role);
    setRoleOptionMenuAnchor(event.currentTarget);
  };

  const handleRoleOptionMenuClose = () => {
    setRoleOptionMenuAnchor(null);
  };

  const handleRoleDetach = async () => {
    try {
      const positionObj = structureData.positions.find(p => p.label === selectedPosition);
      if (!positionObj || !roleToDetach) return;

      await organizationApi.detachRoleFromPosition(positionObj.id, roleToDetach.id);
      
      // Refresh data
      const structureResponse = await organizationApi.getStructure(selectedOrgUnitType || undefined);
      setStructureData(structureResponse);
      
      showAlert('Role detached successfully', 'success');
      setIsRoleDetachConfirmationOpen(false);
      handleRoleOptionMenuClose();
    } catch (error) {
      showAlert(error.message || 'Failed to detach role', 'error', error);
    }
  };

  const handleRoleSelection = (role, type) => {
    switch (type) {
      case ROLE_TYPES.FUNCTIONAL:
        setSelectedFunctionalRoles(prev => 
          prev.includes(role.label) ? [] : [role.label]
        );
        break;
      case ROLE_TYPES.ORGANIZATIONAL:
        setSelectedOrgRoles(prev => 
          prev.includes(role.label) ? [] : [role.label]
        );
        break;
      case ROLE_TYPES.TECHNICAL:
        setSelectedTechRoles(prev => 
          prev.includes(role.label) ? [] : [role.label]
        );
        break;
      default:
        break;
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const sortedGrades = [...structureData.grades].sort((a, b) => a.order - b.order);
  const sortedPositions = [...structureData.positions].sort((a, b) => a.order - b.order);

  const getFunctionalRoles = () => {
    if (!selectedPosition) return [];
    const position = structureData.positions.find(p => p.label === selectedPosition);
    if (!position) return [];
    return position.roles
      .filter(role => role.type === ROLE_TYPES.FUNCTIONAL)
      .sort((a, b) => a.order - b.order);
  };

  const getOrganizationalRoles = () => {
    if (!selectedPosition) return [];
    const position = structureData.positions.find(p => p.label === selectedPosition);
    if (!position) return [];
    return position.roles
      .filter(role => role.type === ROLE_TYPES.ORGANIZATIONAL)
      .sort((a, b) => a.order - b.order);
  };

  const getTechnicalRoles = () => {
    if (!selectedPosition) return [];
    const position = structureData.positions.find(p => p.label === selectedPosition);
    if (!position) return [];
    return position.roles
      .filter(role => role.type === ROLE_TYPES.TECHNICAL)
      .sort((a, b) => a.order - b.order);
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Organizational Structure Configuration</Title>
        </HeaderLeft>
        <div style={{ display: 'flex', gap: '8px' }}>
          {activeTab === TABS.STRUCTURE && (
            <HeaderButton onClick={() => setShowLegend(!showLegend)}>
              {showLegend ? <FaBookOpen /> : <FaBook />}
              {showLegend ? 'Hide Legend' : 'Show Legend'}
            </HeaderButton>
          )}
        </div>
      </Header>

      <TabContainer>
        <TabButton
          data-active={activeTab === TABS.STRUCTURE}
          onClick={() => setActiveTab(TABS.STRUCTURE)}
        >
          Structure
        </TabButton>
        <TabButton
          data-active={activeTab === TABS.ROLES}
          onClick={() => setActiveTab(TABS.ROLES)}
        >
          Roles
        </TabButton>
      </TabContainer>

      <Content>
        {activeTab === TABS.STRUCTURE ? (
          <StructureWrapper>
            <div className="org-unit-type-select">
              <OrgUnitTypeSelector
                value={selectedOrgUnitType}
                onChange={setSelectedOrgUnitType}
                label="Organizational Structure Type"
                required={false}
                showEmptyOption={true}
                emptyOptionLabel="Select organizational type..."
                sx={{ 
                  width: 300,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white'
                  }
                }}
              />
            </div>
            {!selectedOrgUnitType ? (
              <div style={{ marginTop: '40px' }}>
                <EmptyState
                  title="No Organizational Structure Type Selected"
                  description="Please select an organizational structure type from the dropdown above to view and manage positions, grades, and roles."
                />
              </div>
            ) : (
              <div className="sections-container">
                <ConfigurationSection>
                  <SectionTitle type="grade">
                    grade
                    <FaPlus 
                      style={{ 
                        marginLeft: '8px', 
                        cursor: 'pointer',
                        fontSize: '14px'
                      }} 
                      onClick={() => handleGradeModalOpen('add')}
                    />
                  </SectionTitle>
                  <OptionsContainer>
                    {sortedGrades.map((grade) => (
                      <div
                        key={grade.id}
                        style={{ 
                          position: 'relative',
                          width: '100%',
                          marginBottom: '2px'
                        }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, grade)}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, grade)}
                      >
                        <OptionButton
                          type="grade"
                          selected={selectedGrade === grade.label}
                          onClick={() => handleGradeSelect(grade.label)}
                          style={{ 
                            width: '100%',
                            height: '54px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'relative',
                            padding: '0 16px'
                          }}
                        >
                          <span style={{ 
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 'fit-content'
                          }}>
                            {grade.label}
                          </span>
                          <div style={{ 
                            width: '32px',
                            height: '32px',
                            marginLeft: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {selectedGrade === grade.label && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGradeMenuOpen(e, grade);
                                }}
                                style={{ 
                                  padding: 0,
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <FaEllipsisV size={14} />
                              </IconButton>
                            )}
                          </div>
                        </OptionButton>
                      </div>
                    ))}
                  </OptionsContainer>
                </ConfigurationSection>

                <ConfigurationSection>
                  <SectionTitle type="position">
                    position
                    <FaPlus 
                      style={{ 
                        marginLeft: '8px', 
                        cursor: 'pointer',
                        fontSize: '14px'
                      }} 
                      onClick={() => handlePositionModalOpen('add')}
                    />
                  </SectionTitle>
                  <OptionsContainer>
                    {sortedPositions.map((position) => (
                      <div
                        key={position.id}
                        style={{ 
                          position: 'relative',
                          width: '100%',
                          marginBottom: '2px'
                        }}
                        draggable
                        onDragStart={(e) => handlePositionDragStart(e, position)}
                        onDragEnd={handlePositionDragEnd}
                        onDragOver={handlePositionDragOver}
                        onDrop={(e) => handlePositionDrop(e, position)}
                      >
                        <OptionButton
                          type="position"
                          selected={selectedPosition === position.label}
                          onClick={() => handlePositionSelect(position.label)}
                          style={{ 
                            width: '100%',
                            height: '54px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'relative',
                            padding: '0 16px'
                          }}
                        >
                          <span style={{ 
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 'fit-content'
                          }}>
                            {position.label}
                          </span>
                          <div style={{ 
                            width: '32px',
                            height: '32px',
                            marginLeft: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {selectedPosition === position.label && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePositionMenuOpen(e, position);
                                }}
                                style={{ 
                                  padding: 0,
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <FaEllipsisV size={14} />
                              </IconButton>
                            )}
                          </div>
                        </OptionButton>
                      </div>
                    ))}
                  </OptionsContainer>
                </ConfigurationSection>

                <ConfigurationSection>
                  <SectionTitle type="functional">
                    functional role
                    <FaPlus 
                      style={{ 
                        marginLeft: '8px', 
                        cursor: 'pointer',
                        fontSize: '14px'
                      }} 
                      onClick={() => handleRoleLinkClick(ROLE_TYPES.FUNCTIONAL)}
                    />
                  </SectionTitle>
                  <OptionsContainer>
                    {getFunctionalRoles().map((role) => (
                      <div
                        key={role.id}
                        style={{ 
                          position: 'relative',
                          width: '100%',
                          marginBottom: '2px'
                        }}
                      >
                        <OptionButton
                          type="functional"
                          selected={selectedFunctionalRoles.includes(role.label)}
                          onClick={() => handleRoleSelection(role, ROLE_TYPES.FUNCTIONAL)}
                          style={{ 
                            width: '100%',
                            height: '54px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'relative',
                            padding: '0 16px'
                          }}
                        >
                          <span style={{ 
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 'fit-content'
                          }}>
                            {role.label}
                          </span>
                          <div style={{ 
                            width: '32px',
                            height: '32px',
                            marginLeft: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {selectedFunctionalRoles.includes(role.label) && (
                              <IconButton
                                size="small"
                                onClick={(e) => handleRoleOptionMenuOpen(e, role)}
                                style={{ 
                                  padding: 0,
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <FaEllipsisV size={14} />
                              </IconButton>
                            )}
                          </div>
                        </OptionButton>
                      </div>
                    ))}
                  </OptionsContainer>
                </ConfigurationSection>

                <ConfigurationSection>
                  <SectionTitle type="organizational">
                    organizational role
                    <FaPlus 
                      style={{ 
                        marginLeft: '8px', 
                        cursor: 'pointer',
                        fontSize: '14px'
                      }} 
                      onClick={() => handleRoleLinkClick(ROLE_TYPES.ORGANIZATIONAL)}
                    />
                  </SectionTitle>
                  <OptionsContainer>
                    {getOrganizationalRoles().map((role) => (
                      <div
                        key={role.id}
                        style={{ 
                          position: 'relative',
                          width: '100%',
                          marginBottom: '2px'
                        }}
                      >
                        <OptionButton
                          type="organizational"
                          selected={selectedOrgRoles.includes(role.label)}
                          onClick={() => handleRoleSelection(role, ROLE_TYPES.ORGANIZATIONAL)}
                          style={{ 
                            width: '100%',
                            height: '54px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'relative',
                            padding: '0 16px'
                          }}
                        >
                          <span style={{ 
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 'fit-content'
                          }}>
                            {role.label}
                          </span>
                          <div style={{ 
                            width: '32px',
                            height: '32px',
                            marginLeft: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {selectedOrgRoles.includes(role.label) && (
                              <IconButton
                                size="small"
                                onClick={(e) => handleRoleOptionMenuOpen(e, role)}
                                style={{ 
                                  padding: 0,
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <FaEllipsisV size={14} />
                              </IconButton>
                            )}
                          </div>
                        </OptionButton>
                      </div>
                    ))}
                  </OptionsContainer>
                </ConfigurationSection>

                <ConfigurationSection>
                  <SectionTitle type="technical">
                    technical role
                    <FaPlus 
                      style={{ 
                        marginLeft: '8px', 
                        cursor: 'pointer',
                        fontSize: '14px'
                      }} 
                      onClick={() => handleRoleLinkClick(ROLE_TYPES.TECHNICAL)}
                    />
                  </SectionTitle>
                  <OptionsContainer>
                    {getTechnicalRoles().map((role) => (
                      <div
                        key={role.id}
                        style={{ 
                          position: 'relative',
                          width: '100%',
                          marginBottom: '2px'
                        }}
                      >
                        <OptionButton
                          type="technical"
                          selected={selectedTechRoles.includes(role.label)}
                          onClick={() => handleRoleSelection(role, ROLE_TYPES.TECHNICAL)}
                          style={{ 
                            width: '100%',
                            height: '54px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'relative',
                            padding: '0 16px'
                          }}
                        >
                          <span style={{ 
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 'fit-content'
                          }}>
                            {role.label}
                          </span>
                          <div style={{ 
                            width: '32px',
                            height: '32px',
                            marginLeft: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {selectedTechRoles.includes(role.label) && (
                              <IconButton
                                size="small"
                                onClick={(e) => handleRoleOptionMenuOpen(e, role)}
                                style={{ 
                                  padding: 0,
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <FaEllipsisV size={14} />
                              </IconButton>
                            )}
                          </div>
                        </OptionButton>
                      </div>
                    ))}
                  </OptionsContainer>
                </ConfigurationSection>
              </div>
            )}
          </StructureWrapper>
        ) : (
          <ContentWrapper>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              height: '100%',
              width: '100%'
            }}>
              <RolesHeader>
                <SearchContainer style={{ marginBottom: 0 }}>
                  <SearchInput
                    type="text"
                    placeholder="Search roles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <SearchIcon>
                    <FaSearch size={14} />
                  </SearchIcon>
                </SearchContainer>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {Object.values(ROLE_TYPES).map(type => (
                    <HeaderButton
                      key={type}
                      onClick={() => handleRoleTypeToggle(type)}
                      style={{
                        backgroundColor: selectedRoleTypes.includes(type) 
                          ? ROLE_TYPE_COLORS[type].backgroundSelected 
                          : 'white',
                        color: selectedRoleTypes.includes(type) 
                          ? 'white' 
                          : ROLE_TYPE_COLORS[type].text,
                        borderColor: ROLE_TYPE_COLORS[type].text
                      }}
                    >
                      {type}
                    </HeaderButton>
                  ))}
                </div>
                <HeaderButton onClick={() => handleRoleModalOpen('add')}>
                  <FaPlus size={14} />
                  Add Role
                </HeaderButton>
              </RolesHeader>
              <RolesContent>
                <RolesContainer>
                  {getFilteredRoles().map(role => (
                    <RoleCard key={role.id}>
                      <RoleCardContent>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          marginBottom: '8px'
                        }}>
                          <div>
                            <div style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: '#212529',
                              marginBottom: '4px' 
                            }}>
                              {role.label}
                            </div>
                            <div style={{ 
                              fontSize: '12px',
                              color: ROLE_TYPE_COLORS[role.type].text,
                              backgroundColor: ROLE_TYPE_COLORS[role.type].background,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              display: 'inline-block'
                            }}>
                              {role.type}
                            </div>
                          </div>
                          <IconButton
                            size="small"
                            onClick={(event) => handleRoleMenuOpen(event, role)}
                            style={{ padding: 4 }}
                          >
                            <FaEllipsisV size={14} />
                          </IconButton>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          height: '100%',
                          justifyContent: 'space-between'
                        }}>
                          <div>
                            <div style={{ 
                              fontSize: '14px',
                              color: '#666',
                              marginBottom: '16px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: '1.4'
                            }}>
                              {role.description || (
                                <span style={{ fontStyle: 'italic', color: '#999' }}>
                                  No description available
                                </span>
                              )}
                            </div>
                          </div>
                          <div>
                            <div style={{ 
                              fontSize: '12px',
                              fontWeight: '500',
                              marginBottom: '4px',
                              color: '#666'
                            }}>
                              Assigned to positions:
                            </div>
                            <div style={{ 
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '4px'
                            }}>
                              {getPositionsForRole(role.id).map(position => (
                                <div
                                  key={position}
                                  style={{
                                    fontSize: '12px',
                                    backgroundColor: '#e3f2fd',
                                    color: '#2196f3',
                                    padding: '2px 8px',
                                    borderRadius: '4px'
                                  }}
                                >
                                  {position}
                                </div>
                              ))}
                              {getPositionsForRole(role.id).length === 0 && (
                                <div style={{ 
                                  fontSize: '12px',
                                  color: '#999',
                                  fontStyle: 'italic'
                                }}>
                                  Not assigned to any position
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </RoleCardContent>
                    </RoleCard>
                  ))}
                </RolesContainer>
              </RolesContent>
            </div>
          </ContentWrapper>
        )}

        {showLegend && (
          <>
            <LegendSection>
              <LegendTitle type="grade">grade</LegendTitle>
              <LegendGrid>
                {legendData.gradeLegend.map(({ grade, description }) => (
                  <LegendItem key={grade} type="grade">
                    <div>{grade}</div>
                    <div>{description}</div>
                  </LegendItem>
                ))}
              </LegendGrid>
            </LegendSection>

            <LegendSection>
              <LegendTitle type="role">role</LegendTitle>
              <LegendGrid>
                {legendData.roleLegend.map(({ role, description }) => (
                  <LegendItem key={role} type="role">
                    <div>{role}</div>
                    <div>{description}</div>
                  </LegendItem>
                ))}
              </LegendGrid>
            </LegendSection>
          </>
        )}
      </Content>

      <Modal
        isOpen={isGradeModalOpen}
        onClose={handleGradeModalClose}
        onSubmit={handleGradeModalSubmit}
        title={gradeModalMode === 'add' ? 'Add Grade' : 'Edit Grade'}
      >
        <Field>
          <Label>Label</Label>
          <TextField
            fullWidth
            value={gradeModalData.label}
            onChange={(e) => setGradeModalData({ ...gradeModalData, label: e.target.value })}
            size="small"
          />
        </Field>
        <Field>
          <Label>Description</Label>
          <TextField
            fullWidth
            value={gradeModalData.description}
            onChange={(e) => setGradeModalData({ ...gradeModalData, description: e.target.value })}
            size="small"
            multiline
            rows={3}
          />
        </Field>
      </Modal>

      <Menu
        anchorEl={gradeMenuAnchor}
        open={Boolean(gradeMenuAnchor)}
        onClose={handleGradeMenuClose}
      >
        <MenuItem onClick={() => {
          const grade = structureData.grades.find(g => g.id === selectedGradeId);
          handleGradeModalOpen('edit', grade);
          handleGradeMenuClose();
        }}>
          Edit
        </MenuItem>
        <MenuItem 
          onClick={() => {
            const grade = structureData.grades.find(g => g.id === selectedGradeId);
            handleDeleteClick(grade);
          }} 
          style={{ 
            color: COLORS.DESTRUCTIVE,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <ConfirmationModal
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        onConfirm={handleGradeDelete}
        title="Delete Grade"
        message={`Are you sure you want to delete the grade "${gradeToDelete?.label}"?`}
        actionButtonText="Delete"
      />

      <Modal
        isOpen={isPositionModalOpen}
        onClose={handlePositionModalClose}
        onSubmit={handlePositionModalSubmit}
        title={positionModalMode === 'add' ? 'Add Position' : 'Edit Position'}
      >
        <Field>
          <Label>Label</Label>
          <TextField
            fullWidth
            value={positionModalData.label}
            onChange={(e) => setPositionModalData({ ...positionModalData, label: e.target.value })}
            size="small"
          />
        </Field>
        <Field>
          <Label>Description</Label>
          <TextField
            fullWidth
            value={positionModalData.description}
            onChange={(e) => setPositionModalData({ ...positionModalData, description: e.target.value })}
            size="small"
            multiline
            rows={3}
          />
        </Field>
        <Field>
          <Label>Requires Grade</Label>
          <FormControl fullWidth size="small">
            <Select
              value={positionModalData.requiresGrade}
              onChange={(e) => setPositionModalData({ ...positionModalData, requiresGrade: e.target.value })}
            >
              <MenuItem value={true}>Yes</MenuItem>
              <MenuItem value={false}>No</MenuItem>
            </Select>
          </FormControl>
        </Field>
      </Modal>

      <Menu
        anchorEl={positionMenuAnchor}
        open={Boolean(positionMenuAnchor)}
        onClose={handlePositionMenuClose}
      >
        <MenuItem onClick={() => {
          const position = structureData.positions.find(p => p.id === selectedPositionId);
          handlePositionModalOpen('edit', position);
          handlePositionMenuClose();
        }}>
          Edit
        </MenuItem>
        <MenuItem 
          onClick={() => {
            const position = structureData.positions.find(p => p.id === selectedPositionId);
            handlePositionDeleteClick(position);
          }} 
          style={{ 
            color: COLORS.DESTRUCTIVE,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <ConfirmationModal
        isOpen={isPositionDeleteConfirmationOpen}
        onClose={() => setIsPositionDeleteConfirmationOpen(false)}
        onConfirm={handlePositionDelete}
        title="Delete Position"
        message={`Are you sure you want to delete the position "${positionToDelete?.label}"?`}
        actionButtonText="Delete"
      />

      <Modal
        isOpen={isRoleModalOpen}
        onClose={handleRoleModalClose}
        onSubmit={handleRoleModalSubmit}
        title={roleModalMode === 'add' ? 'Add Role' : 'Edit Role'}
      >
        <Field>
          <Label>Label</Label>
          <TextField
            fullWidth
            value={roleModalData.label}
            onChange={(e) => setRoleModalData({ ...roleModalData, label: e.target.value })}
            size="small"
          />
        </Field>
        <Field>
          <Label>Description</Label>
          <TextField
            fullWidth
            value={roleModalData.description}
            onChange={(e) => setRoleModalData({ ...roleModalData, description: e.target.value })}
            size="small"
            multiline
            rows={3}
          />
        </Field>
        <Field>
          <Label>Type</Label>
          <TextField
            select
            fullWidth
            value={roleModalData.type}
            onChange={(e) => setRoleModalData({ ...roleModalData, type: e.target.value })}
            size="small"
          >
            {Object.values(ROLE_TYPES).map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </Field>
      </Modal>

      <Menu
        anchorEl={roleMenuAnchor}
        open={Boolean(roleMenuAnchor)}
        onClose={handleRoleMenuClose}
      >
        <MenuItem onClick={() => {
          const role = structureData.roles.find(r => r.id === selectedRoleId);
          handleRoleModalOpen('edit', role);
          handleRoleMenuClose();
        }}>
          Edit
        </MenuItem>
        <MenuItem 
          onClick={() => {
            setIsRoleDeleteConfirmationOpen(true);
            handleRoleMenuClose();
          }} 
          style={{ 
            color: COLORS.DESTRUCTIVE,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <ConfirmationModal
        isOpen={isRoleDeleteConfirmationOpen}
        onClose={() => setIsRoleDeleteConfirmationOpen(false)}
        onConfirm={handleRoleDelete}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${roleToDelete?.label}"?`}
        actionButtonText="Delete"
      />

      <Modal
        isOpen={isRoleSelectionModalOpen}
        onClose={() => setIsRoleSelectionModalOpen(false)}
        title={roleSelectionType === ROLE_TYPES.ORGANIZATIONAL 
          ? `Select ${roleSelectionType.toLowerCase()} role`
          : `Select ${roleSelectionType?.toLowerCase()} role for position "${selectedPosition}"`
        }
        submitText="Attach"
      >
        <div style={{ marginBottom: '16px' }}>
          <SearchContainer style={{ marginBottom: '16px' }}>
            <SearchInput
              type="text"
              placeholder={`Search ${roleSelectionType?.toLowerCase()} roles...`}
              value={roleSearchQuery}
              onChange={(e) => setRoleSearchQuery(e.target.value)}
            />
            <SearchIcon>
              <FaSearch size={14} />
            </SearchIcon>
          </SearchContainer>
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {getAvailableRolesForType(roleSelectionType)?.map(role => (
              <div
                key={role.id}
                onClick={() => {
                  handleRoleSelect(role);
                  setIsRoleSelectionModalOpen(false);
                }}
                style={{
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: '#f8f9fa',
                  '&:hover': {
                    backgroundColor: '#f1f3f5',
                    borderColor: '#bdbdbd'
                  }
                }}
              >
                <div style={{ 
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '4px'
                }}>
                  {role.label}
                </div>
                {role.description && (
                  <div style={{ 
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    {role.description}
                  </div>
                )}
              </div>
            ))}
            {getAvailableRolesForType(roleSelectionType)?.length === 0 && (
              <div style={{ 
                padding: '16px',
                textAlign: 'center',
                color: '#666',
                fontStyle: 'italic'
              }}>
                No available roles found
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Menu
        anchorEl={roleOptionMenuAnchor}
        open={Boolean(roleOptionMenuAnchor)}
        onClose={handleRoleOptionMenuClose}
      >
        <MenuItem 
          onClick={() => {
            setIsRoleDetachConfirmationOpen(true);
            handleRoleOptionMenuClose();
          }}
          style={{ 
            color: COLORS.DESTRUCTIVE,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Detach
        </MenuItem>
      </Menu>

      <ConfirmationModal
        isOpen={isRoleDetachConfirmationOpen}
        onClose={() => setIsRoleDetachConfirmationOpen(false)}
        onConfirm={handleRoleDetach}
        title="Detach Role"
        message={`Are you sure you want to detach the role "${roleToDetach?.label}" from position "${selectedPosition}"?`}
        actionButtonText="Detach"
      />
    </Container>
  );
};

export default EmployeeOrgConfiguration;